import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputBase,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { checkPermission } from "../../../helpers/helpers";
import { apiConstants } from "../../../constants";
import { authHeader } from "../../../helpers";
import ShopifyTable from "../../../components/admin/ShopifyManagement/ShopifyTable/ShopifyTable.component";
import BoxedContent from "../../../components/layout/pageLayout/BoxedContent/BoxedContent.component";
import Content from "../../../components/layout/pageLayout/Content/Content.component";
import Intro from "../../../components/atoms/Intro/Intro.component";
import { PERMISSIONS } from "../../../constants/data/rolesPermissions.constants";

function PageWidgets({ searchLabel, setSearchValue }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder={`Search ${searchLabel}`}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </Search>

        <Button
          variant="contained"
          startIcon={<AddCircleOutlineOutlinedIcon />}
          onClick={handleClickOpen}
          size="large"
        >
          Create a New Shopify Organization
        </Button>

        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate("/admin/shopify-management/apps/list")}
        >
          Shopify Apps
        </Button>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Creating a new Shopify Organization</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please navigate to Organization Management and activate “Enable
              Shopify Store” in organisation properties to set up a Shopify
              Organization for this client. Only already-created organisations
              appear here.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                handleClose();
                navigate("/admin/organization-management");
              }}
              autoFocus
            >
              Go to Organization Management
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
}

export default function ShopifyList() {
  const { parameter } = useParams();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [activeAppName, setActiveAppName] = useState("");

  // permission check
  useEffect(() => {
    if (!checkPermission(PERMISSIONS.WEB_STORE_MANAGEMENT)) {
      navigate("/");
    }
  }, [navigate]);

  // fetch active-app once
  useEffect(() => {
    fetch(`${apiConstants.API_URL}/shopify_apps/active-app`, {
      headers: authHeader(),
    })
      .then((r) => r.json())
      .then((data) => setActiveAppName(data?.data?.name || ""))
      .catch(() => setActiveAppName(""));
  }, []);

  const breadcrumbs = [
    <Typography key="1">Shopify Stores</Typography>,
    <Typography key="2">All Shopify Organization</Typography>,
  ];

  const pageTitle = activeAppName
    ? `All Shopify Organization - ${activeAppName}`
    : "All Shopify Organization";

  return (
    <>
      <Intro
        pageTitle={pageTitle}
        pageTitleShort="All Shopify Organization"
        breadcrumbs={breadcrumbs}
        additionalWidgets={
          <PageWidgets searchLabel="All Shopify Organization" setSearchValue={setSearchValue} />
        }
      />

      <Content>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BoxedContent title="All Shopify Organization" subtitle="" description="">
              <ShopifyTable search={searchValue} />
            </BoxedContent>
          </Grid>
        </Grid>
      </Content>
    </>
  );
}


// — styled components —

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.3s ease-in-out",
  color: "#232323",
  backgroundColor: alpha(theme.palette.common.black, 0.05),
  "&:hover, &:focus": {
    backgroundColor: alpha(theme.palette.common.black, 0.15),
  },
  marginRight: theme.spacing(1),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    fontSize: "0.85rem",
    width: "100%",
    height: "1.85rem",
    [theme.breakpoints.up("md")]: {
      minWidth: "10rem",
    },
  },
}));
